import { useMemo, useState } from 'react';
import type { LapRef, ResampledLap } from '@tla/types';
import { useCursorStore } from '../../store/cursorStore';
import { useWorkspaceStore } from '../../store/workspaceStore';
import { lapKey } from '../../store/colours';

/** Classification of a stretch of track for a lap. */
type SegmentKind = 'brake' | 'corner' | 'straight';

interface Segment {
  kind: SegmentKind;
  /** Distance (m) at the segment start / end. */
  d0: number;
  d1: number;
  /** Short label, e.g. "0.72G", "R|1.04G", "153km/h". */
  label: string;
}

const KIND_COLOUR: Record<SegmentKind, string> = {
  brake: '#ff5c5c',
  corner: '#4aa3ff',
  straight: '#4ade80',
};

// Classification thresholds (in G / m·s⁻²). Longitudinal accel is negative when
// braking; lateral accel magnitude marks cornering.
const BRAKE_G = 0.18;
const CORNER_G = 0.3;
/** Merge away segments shorter than this many metres to reduce noise. */
const MIN_SEGMENT_M = 25;

function classify(lon: number, lat: number): SegmentKind {
  if (Number.isFinite(lon) && lon < -BRAKE_G) return 'brake';
  if (Number.isFinite(lat) && Math.abs(lat) > CORNER_G) return 'corner';
  return 'straight';
}

/** Detect braking / cornering / straight segments along a resampled lap. */
function detectSegments(lap: ResampledLap): Segment[] {
  const dist = lap.distance;
  const lon = lap.channels['longitudinal_acc'];
  const lat = lap.channels['lateral_acc'];
  const speed = lap.channels['speed_gps'];
  const n = dist.length;
  if (n < 2) return [];

  // Per-sample classification.
  const kinds: SegmentKind[] = new Array(n);
  for (let i = 0; i < n; i++) {
    kinds[i] = classify(lon ? lon[i] : NaN, lat ? lat[i] : NaN);
  }

  // Merge consecutive samples of the same kind into raw segments.
  interface Raw {
    kind: SegmentKind;
    i0: number;
    i1: number;
  }
  const raw: Raw[] = [];
  for (let i = 0; i < n; i++) {
    const last = raw[raw.length - 1];
    if (last && last.kind === kinds[i]) last.i1 = i;
    else raw.push({ kind: kinds[i], i0: i, i1: i });
  }

  // Absorb very short segments into the previous one to reduce fragmentation.
  const merged: Raw[] = [];
  for (const seg of raw) {
    const lenM = dist[seg.i1] - dist[seg.i0];
    const prev = merged[merged.length - 1];
    if (prev && lenM < MIN_SEGMENT_M) {
      prev.i1 = seg.i1;
    } else {
      merged.push({ ...seg });
    }
  }

  // Build labelled segments with a peak metric per kind.
  return merged.map((seg) => {
    let peakLon = 0;
    let peakLat = 0;
    let peakSpeed = 0;
    for (let i = seg.i0; i <= seg.i1; i++) {
      if (lon && lon[i] < peakLon) peakLon = lon[i];
      if (lat && Math.abs(lat[i]) > Math.abs(peakLat)) peakLat = lat[i];
      if (speed && speed[i] > peakSpeed) peakSpeed = speed[i];
    }
    let label: string;
    if (seg.kind === 'brake') {
      label = `${Math.abs(peakLon).toFixed(2)}G`;
    } else if (seg.kind === 'corner') {
      // Positive lateral acceleration is measured towards the outside of a
      // left-hand turn, so a positive peak means the car is turning LEFT.
      const dir = peakLat >= 0 ? 'L' : 'R';
      label = `${dir}|${Math.abs(peakLat).toFixed(2)}G`;
    } else {
      label = `${Math.round(peakSpeed * 3.6)}km/h`;
    }
    return { kind: seg.kind, d0: dist[seg.i0], d1: dist[seg.i1], label };
  });
}

/**
 * Lap Events Timeline: per selected lap, a horizontal band of detected braking,
 * cornering and straight segments along the lap distance. All laps share the
 * distance scale (they are resampled onto the reference grid), so the bands line
 * up for direct comparison. Respects the active zoom range and drives the shared
 * cursor on hover; clicking a segment zooms every view to that stretch.
 */
export function LapEventsTimeline(props: {
  laps: { ref: LapRef; colour: string; lap: ResampledLap }[];
  labels: Record<string, string>;
}): React.ReactElement | null {
  // Collapsed by default on mobile (matches the app's 820px breakpoint) where
  // vertical space is scarce; expanded by default on desktop.
  const [open, setOpen] = useState(
    () => !(typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches),
  );
  const zoom = useWorkspaceStore((s) => s.zoom);
  const setZoom = useWorkspaceStore((s) => s.setZoom);
  const setActive = useCursorStore((s) => s.setActive);
  const trackId = useWorkspaceStore((s) => s.trackId);
  const tracks = useWorkspaceStore((s) => s.tracks);
  const corners = useMemo(
    () => (trackId ? (tracks[trackId]?.corners ?? []) : []),
    [trackId, tracks],
  );

  const rows = useMemo(
    () =>
      props.laps.map((o) => ({
        key: lapKey(o.ref),
        colour: o.colour,
        label: props.labels[lapKey(o.ref)] ?? `Lap ${o.ref.lapNumber}`,
        segments: detectSegments(o.lap),
      })),
    [props.laps, props.labels],
  );

  if (rows.length === 0) return null;

  const first = props.laps[0]?.lap.distance;
  const fullSpan: [number, number] =
    first && first.length > 0 ? [first[0], first[first.length - 1]] : [0, 1];
  const [vd0, vd1] = zoom ?? fullSpan;
  const span = vd1 - vd0 || 1;

  const onTrackMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = (e.clientX - rect.left) / (rect.width || 1);
    setActive(vd0 + frac * span);
  };

  return (
    <div className={`events-panel${open ? '' : ' collapsed'}`}>
      <div className="events-head">
        <button className="events-toggle" onClick={() => setOpen((v) => !v)}>
          {open ? '▾' : '▸'} Lap events
        </button>
        <div className="events-legend">
          <span className="ev-key">
            <span className="ev-swatch" style={{ background: KIND_COLOUR.brake }} /> Braking
          </span>
          <span className="ev-key">
            <span className="ev-swatch" style={{ background: KIND_COLOUR.corner }} /> Cornering
          </span>
          <span className="ev-key">
            <span className="ev-swatch" style={{ background: KIND_COLOUR.straight }} /> Straight
          </span>
        </div>
      </div>

      {open && (
        <div className="events-body">
          {rows.map((row) => (
            <div className="event-row" key={row.key}>
              <div className="event-label" title={row.label}>
                <span className="ev-dot" style={{ background: row.colour }} />
                {row.label}
              </div>
              <div className="event-track" onMouseMove={onTrackMove} onMouseLeave={() => setActive(null)}>
                {row.segments.map((seg, i) => {
                  const c0 = Math.max(seg.d0, vd0);
                  const c1 = Math.min(seg.d1, vd1);
                  if (c1 <= c0) return null;
                  const left = ((c0 - vd0) / span) * 100;
                  const width = ((c1 - c0) / span) * 100;
                  return (
                    <div
                      key={i}
                      className="event-seg"
                      style={{ left: `${left}%`, width: `${width}%`, background: KIND_COLOUR[seg.kind] }}
                      title={`${seg.kind} · ${Math.round(seg.d0)}–${Math.round(seg.d1)} m · ${seg.label}`}
                      onClick={() => setZoom([seg.d0, seg.d1])}
                    >
                      {width > 5 && <span className="event-seg-label">{seg.label}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {corners.length > 0 && (
            <div className="event-row corner-ref-row">
              <div className="event-label" title="Named corners">
                Corners
              </div>
              <div
                className="event-track corner-ref-track"
                onMouseMove={onTrackMove}
                onMouseLeave={() => setActive(null)}
              >
                {corners.map((c) => {
                  const start = c.startDistance ?? c.distance;
                  const end = c.endDistance ?? c.distance;
                  // Skip corners entirely outside the current view.
                  if (end < vd0 || start > vd1) return null;
                  const leftPct = ((start - vd0) / span) * 100;
                  const rightPct = ((end - vd0) / span) * 100;
                  const widthPct = Math.max(0, rightPct - leftPct);
                  const zoomTo = (): void =>
                    setZoom([
                      Math.max(fullSpan[0], start - 60),
                      Math.min(fullSpan[1], end + 60),
                    ]);
                  return (
                    <div
                      key={c.id}
                      className="corner-span"
                      style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                      title={`${c.name} · ${Math.round(start)}–${Math.round(end)} m`}
                      onClick={zoomTo}
                    >
                      <span className="corner-edge corner-edge-start" />
                      <span className="corner-span-label">{c.name}</span>
                      <span className="corner-edge corner-edge-end" />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
